import { useEffect, useRef, useState } from "react";
import { assistantAction, uploadReference, watchAssistant } from "../../services/artworkAssistant";
import { artworkObjectUrl } from "../../services/vehicleArtwork";
import "./artworkAssistant.css";

export const REVIEW_CHECKS = {
  generation: "Correct generation", proportions: "Correct overall proportions",
  roofline: "Correct roofline", frontRear: "Correct front/rear body",
  intakes: "Correct side intake/scoops", wing: "Correct wing/spoiler where applicable",
  vents: "Correct major factory vents", openArches: "Open wheel arches",
  noWheels: "No wheels/rims", noBackground: "No background", noShading: "No shading/fills",
  laserCut: "Lines suitable for laser-cut artwork",
};
export function PrivateArtworkImage({ assetPath, alt }) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    let local;
    setUrl(""); setError("");
    if (assetPath) artworkObjectUrl(assetPath).then((value) => {
      local = value;
      if (active) setUrl(value); else URL.revokeObjectURL(value);
    }).catch(() => { if (active) setError("Image unavailable. Check Storage access/CORS."); });
    return () => { active = false; if (local) URL.revokeObjectURL(local); };
  }, [assetPath]);
  return error ? <p role="alert">{error}</p> : url ?
    <img className="artwork-image" src={url} alt={alt} /> : <p>Loading image…</p>;
}
export function ReferenceProvenance({ reference }) {
  if (!reference) return null;
  return <div className="artwork-provenance">
    <p>{reference.title || "Reference image"} · {reference.provider || "manual"} · {reference.sourceDomain}</p>
    {reference.sourceUrl && <a href={reference.sourceUrl} target="_blank" rel="noreferrer">Open source</a>}
    <p>Rights: {reference.rights || "Not supplied — verify permission before approval."}</p>
    {reference.attribution && <p>Attribution: {reference.attribution}</p>}
    {reference.retrievedAt && <p>Retrieved: {reference.retrievedAt}</p>}
    {reference.sourceNotes && <p>Notes: {reference.sourceNotes}</p>}
  </div>;
}
export function GeneratedVersionReview({ version, artworkId, busy, run }) {
  const [checks, setChecks] = useState({});
  const removableBackground = version.requiresBackgroundRemoval || version.pendingNormalization?.backgroundRemoved;
  const complete = Object.keys(REVIEW_CHECKS).every((name) => checks[name]);
  return <div>
    <div className="artwork-comparison">
      <section><h4>Reference used for this generation</h4>
        <PrivateArtworkImage assetPath={version.source.assetPath} alt="Selected vehicle body reference" />
        <ReferenceProvenance reference={version.source} /></section>
      <section><h4>Generated silhouette · {version.status}</h4>
        <PrivateArtworkImage assetPath={version.assetPath} alt="Generated silhouette awaiting human inspection" />
        <p>Prompt: {version.source.promptVersion} · {version.source.model}</p>
        {removableBackground && <p>A light canvas was detected. Approval will remove it while preserving the contour. Inspect for any scene, shadows, or unwanted marks before continuing.</p>}
      </section>
    </div>
    {version.status === "draft" && <>
      <fieldset className="artwork-checklist" disabled={busy}><legend>Required human review</legend>
        {Object.entries(REVIEW_CHECKS).map(([name, label]) => <label key={name}>
          <input type="checkbox" checked={Boolean(checks[name])}
            onChange={(event) => setChecks({ ...checks, [name]: event.target.checked })} />{name === "noBackground" && removableBackground ? "No background scene beyond the removable light canvas" : label}
        </label>)}
      </fieldset>
      <div className="plaque-actions">
        <button className="plaque-button" disabled={busy || !complete} onClick={() => run(() =>
          assistantAction("reviewVehicleArtwork", { artworkId, version: version.id, checklist: checks }), "Version moved to review. Approval is a separate step.")}>Move to review</button>
        <button className="plaque-button" disabled={busy} onClick={() => run(() =>
          assistantAction("updateArtworkDraft", { artworkId, version: version.id, action: "prefer" }), "Preferred draft selected.")}>Mark as preferred draft</button>
        <button className="plaque-button" disabled={busy} onClick={() => run(() =>
          assistantAction("updateArtworkDraft", { artworkId, version: version.id, action: "archive" }), "Draft archived.")}>Archive draft</button>
      </div>
    </>}
  </div>;
}

export default function ArtworkAssistant({ artwork, onCompleted }) {
  const [capabilities, setCapabilities] = useState({});
  const [data, setData] = useState({ state: {}, references: [], jobs: [] });
  const [candidates, setCandidates] = useState([]);
  const [imageUrl, setImageUrl] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const callback = useRef(onCompleted);
  callback.current = onCompleted;
  const seenCompleted = useRef(new Set());
  useEffect(() => {
    let active = true;
    assistantAction("artworkAssistantCapabilities").then((value) => { if (active) setCapabilities(value); })
      .catch((failure) => { if (active) setError(failure.message); });
    const stop = watchAssistant(artwork.id, (name, value) => {
      setData((previous) => ({ ...previous, [name]: value }));
      if (name === "jobs") value.forEach((job) => {
        if (job.status === "completed" && !seenCompleted.current.has(job.id)) {
          seenCompleted.current.add(job.id); callback.current();
        }
      });
    }, (failure) => setError(failure.message));
    return () => { active = false; stop(); };
  }, [artwork.id]);
  const reference = data.references.find((item) => item.id === data.state.activeReferenceId);
  const act = async (operation) => {
    if (busy) return;
    setBusy(true); setError(""); setMessage("");
    try { await operation(); } catch (failure) { setError(failure.message || "Operation failed."); }
    finally { setBusy(false); }
  };
  const select = (candidate) => act(async () => {
    await assistantAction("selectArtworkReference", { ...candidate, artworkId: artwork.id, sourceNotes: notes });
    setMessage("Private reference selected. Check it carefully before generating.");
  });
  const generating = Boolean(data.state.activeJobId);
  return <section className="artwork-assistant" aria-label="Vehicle Artwork Assistant">
    <h3>Reference image</h3>
    <p>Choose an accurate factory side view. Search availability does not establish usage rights.</p>
    {error && <p role="alert" className="plaque-error">{error}</p>}
    <p role="status">{message}</p>
    {!capabilities.search && <p>Web reference search is not configured. Manual URLs and uploads remain available.</p>}
    <button className="plaque-button" disabled={busy || !capabilities.search} onClick={() => act(async () => {
      const result = await assistantAction("searchArtworkReferences", { artworkId: artwork.id });
      setCandidates(result.candidates || []);
      setMessage(result.message || `${result.candidates.length} candidates. Search: ${result.query}. Nothing is selected automatically.`);
    })}>Find reference</button>
    <div className="artwork-candidates">{candidates.map((candidate) => <article key={candidate.providerAsset || candidate.imageUrl}>
      <img src={candidate.thumbnailUrl} alt={candidate.title} referrerPolicy="no-referrer" loading="lazy" />
      <ReferenceProvenance reference={candidate} />
      <button className="plaque-button" disabled={busy} onClick={() => select(candidate)}>Choose this reference</button>
    </article>)}</div>
    <label>Direct reference image URL (PNG/JPEG)<input type="url" maxLength={1000} value={imageUrl} onChange={(event) => setImageUrl(event.target.value)} /></label>
    <label>Source page URL<input type="url" maxLength={1000} value={sourceUrl} onChange={(event) => setSourceUrl(event.target.value)} /></label>
    <label>Reference and usage permission notes<textarea maxLength={1000} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
    <button className="plaque-button" disabled={busy || !imageUrl} onClick={() => select({ imageUrl, sourceUrl, provider: "manual-url" })}>Use reference URL</button>
    <label>Private reference upload (PNG/JPEG, up to 5 MB)<input type="file" accept="image/png,image/jpeg" disabled={busy} onChange={(event) => setFile(event.target.files?.[0] || null)} /></label>
    <button className="plaque-button" disabled={busy || !file} onClick={() => act(async () => {
      await uploadReference(artwork.id, file, { sourceUrl, sourceNotes: notes, provider: "manual-upload" });
      setMessage("Private reference uploaded and selected.");
    })}>Upload reference</button>
    {reference && <section><h4>Active reference</h4>
      <PrivateArtworkImage assetPath={reference.assetPath} alt="Active reference for the next generation" />
      <ReferenceProvenance reference={reference} />
    </section>}
    <h3>Generate silhouette</h3>
    <p>Locked contour prompt v1. Each attempt produces a new draft for human review. Generation may incur provider charges.</p>
    {!capabilities.generation && <p>Generation is not configured. You can still upload and approve a silhouette manually below.</p>}
    <button className="plaque-button primary" disabled={busy || generating || !reference || !capabilities.generation} onClick={() => act(async () => {
      const result = await assistantAction("generateArtworkDraft", { artworkId: artwork.id,
        referenceId: reference.id, requestId: crypto.randomUUID() });
      if (result.status === "failed") throw new Error(result.failureReason);
      setMessage(result.status === "completed" ? "New draft generated. Compare it below before review." : "Generation is running. History updates automatically.");
    })}>{generating ? "Generating…" : data.jobs.length ? "Regenerate new draft" : "Generate silhouette"}</button>
    <p>Preferred draft: {data.state.preferredVersion?.slice(0, 8) || "None"}</p>
    <h4>Generation history</h4>
    {!data.jobs.length && <p>No generation attempts yet.</p>}
    <ol className="artwork-jobs">{data.jobs.map((job) => <li key={job.id}>
      Attempt {job.attempt} · {job.status} · {job.version.slice(0, 8)} · {job.provider}/{job.model}
      {job.failureReason && <p>{job.failureReason}</p>}
    </li>)}</ol>
    {generating && <p>Interrupted attempts expire automatically within approximately ten minutes. Refreshing this page does not start another attempt.</p>}
  </section>;
}
